import { useState } from 'react'
import {
  useAlertRules,
  useCreateAlertRule,
  useDeleteAlertRule,
  useToggleAlertRule,
  useChannels,
  useCreateChannel,
  useDeleteChannel,
  useNotifications,
} from '@/api/notifications'
import { RequirePermission } from '@/components/RequirePermission'
import type { AlertRuleType, ChannelType, NotificationStatus } from '@/types'

interface Props {
  tenantId: string | null
}

type Tab = 'rules' | 'channels' | 'history'

const RULE_TYPE_LABELS: Record<AlertRuleType, string> = {
  metric_threshold: 'Metric threshold',
  device_status: 'Device status',
  operation_failed: 'Operation failed',
}

const NOTIF_STATUS_LABELS: Record<NotificationStatus, string> = {
  pending: 'Pending',
  delivered: 'Delivered',
  failed: 'Failed',
}

// ── CreateRuleForm ─────────────────────────────────────────────────────────────

function CreateRuleForm({
  tenantId,
  onClose,
}: {
  tenantId: string
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState<AlertRuleType>('metric_threshold')
  const [query, setQuery] = useState('')
  const [operator, setOperator] = useState('>')
  const [threshold, setThreshold] = useState('0')
  const [targetStatus, setTargetStatus] = useState('failed')
  const [cooldown, setCooldown] = useState('3600')

  const createRule = useCreateAlertRule(tenantId)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const config: Record<string, unknown> =
      type === 'metric_threshold'
        ? { query, operator, threshold: parseFloat(threshold) }
        : type === 'device_status'
        ? { target_status: targetStatus }
        : {}

    await createRule.mutateAsync({
      name: name.trim(),
      type,
      config,
      cooldown_seconds: parseInt(cooldown, 10) || 3600,
    })
    onClose()
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    padding: '8px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
      }}
    >
      <div style={{ gridColumn: '1 / -1', fontWeight: 600, fontSize: 14 }}>New alert rule</div>

      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
      </div>
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Type</label>
        <select style={inputStyle} value={type} onChange={e => setType(e.target.value as AlertRuleType)}>
          {(Object.keys(RULE_TYPE_LABELS) as AlertRuleType[]).map(t => (
            <option key={t} value={t}>{RULE_TYPE_LABELS[t]}</option>
          ))}
        </select>
      </div>

      {type === 'metric_threshold' && (
        <>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>PromQL query</label>
            <input style={inputStyle} value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g. cpu_usage_percent" required />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Operator</label>
            <select style={inputStyle} value={operator} onChange={e => setOperator(e.target.value)}>
              {['>', '>=', '<', '<=', '=='].map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Threshold</label>
            <input style={inputStyle} type="number" step="any" value={threshold} onChange={e => setThreshold(e.target.value)} required />
          </div>
        </>
      )}

      {type === 'device_status' && (
        <div>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Target status</label>
          <select style={inputStyle} value={targetStatus} onChange={e => setTargetStatus(e.target.value)}>
            {['pending', 'provisioning', 'active', 'maintenance', 'failed', 'decommissioned'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Cooldown (seconds)</label>
        <input style={inputStyle} type="number" min="0" value={cooldown} onChange={e => setCooldown(e.target.value)} />
      </div>

      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={createRule.isPending}>
          {createRule.isPending ? 'Creating…' : 'Create rule'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </form>
  )
}

// ── CreateChannelForm ──────────────────────────────────────────────────────────

function CreateChannelForm({
  tenantId,
  onClose,
}: {
  tenantId: string
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState<ChannelType>('webhook')
  const [url, setUrl] = useState('')

  const createChannel = useCreateChannel(tenantId)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    await createChannel.mutateAsync({
      name: name.trim(),
      type,
      config: type === 'webhook' ? { url: url.trim() } : {},
    })
    onClose()
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    padding: '8px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
      }}
    >
      <div style={{ gridColumn: '1 / -1', fontWeight: 600, fontSize: 14 }}>New channel</div>
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
      </div>
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Type</label>
        <select style={inputStyle} value={type} onChange={e => setType(e.target.value as ChannelType)}>
          <option value="webhook">Webhook</option>
          <option value="email">Email (coming soon)</option>
        </select>
      </div>
      {type === 'webhook' && (
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Webhook URL</label>
          <input style={inputStyle} type="url" value={url} onChange={e => setUrl(e.target.value)} required />
        </div>
      )}
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={createChannel.isPending}>
          {createChannel.isPending ? 'Creating…' : 'Create channel'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </form>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function Notifications({ tenantId }: Props) {
  const [tab, setTab] = useState<Tab>('rules')
  const [showCreateRule, setShowCreateRule] = useState(false)
  const [showCreateChannel, setShowCreateChannel] = useState(false)

  const rulesQuery = useAlertRules(tenantId)
  const toggleRule = useToggleAlertRule(tenantId ?? '')
  const deleteRule = useDeleteAlertRule(tenantId ?? '')

  const channelsQuery = useChannels(tenantId)
  const deleteChannel = useDeleteChannel(tenantId ?? '')

  const notifQuery = useNotifications(tenantId)

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage notifications.</div>
  }

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: tab === t ? 600 : 400,
    cursor: 'pointer',
    background: tab === t ? 'var(--color-accent)' : 'transparent',
    color: tab === t ? '#fff' : 'var(--color-text-muted)',
    border: 'none',
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">Alert rules, channels, delivery history</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'rules' && (
            <RequirePermission perm="admin:access">
              <button className="btn btn-primary" onClick={() => setShowCreateRule(!showCreateRule)}>
                + Add rule
              </button>
            </RequirePermission>
          )}
          {tab === 'channels' && (
            <RequirePermission perm="admin:access">
              <button className="btn btn-primary" onClick={() => setShowCreateChannel(!showCreateChannel)}>
                + Add channel
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        <button style={tabStyle('rules')} onClick={() => setTab('rules')}>
          Rules ({rulesQuery.data?.total ?? 0})
        </button>
        <button style={tabStyle('channels')} onClick={() => setTab('channels')}>
          Channels ({channelsQuery.data?.total ?? 0})
        </button>
        <button style={tabStyle('history')} onClick={() => setTab('history')}>
          History ({notifQuery.data?.total ?? 0})
        </button>
      </div>

      {/* Alert rules tab */}
      {tab === 'rules' && (
        <>
          {showCreateRule && (
            <CreateRuleForm tenantId={tenantId} onClose={() => setShowCreateRule(false)} />
          )}

          {rulesQuery.isLoading && <div className="empty-state">Loading…</div>}
          {rulesQuery.error && (
            <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading rules.</div>
          )}
          {!rulesQuery.isLoading && (rulesQuery.data?.data ?? []).length === 0 && (
            <div className="empty-state">No alert rules. Create one to get started.</div>
          )}
          {(rulesQuery.data?.data ?? []).length > 0 && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Enabled</th>
                    <th>Cooldown</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(rulesQuery.data?.data ?? []).map(rule => (
                    <tr key={rule.id}>
                      <td style={{ fontWeight: 500 }}>{rule.name}</td>
                      <td>
                        <span style={{
                          background: 'var(--color-surface-2)',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 12,
                        }}>
                          {RULE_TYPE_LABELS[rule.type]}
                        </span>
                      </td>
                      <td>
                        <RequirePermission perm="admin:access">
                          <button
                            className={`btn ${rule.enabled ? 'btn-primary' : 'btn-ghost'}`}
                            style={{ padding: '3px 10px', fontSize: 12 }}
                            onClick={() => toggleRule.mutate({ ruleId: rule.id, enabled: !rule.enabled })}
                          >
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </RequirePermission>
                      </td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {rule.cooldown_seconds}s
                      </td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {new Date(rule.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <RequirePermission perm="admin:access">
                          <button
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => deleteRule.mutate(rule.id)}
                          >
                            Delete
                          </button>
                        </RequirePermission>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Channels tab */}
      {tab === 'channels' && (
        <>
          {showCreateChannel && (
            <CreateChannelForm tenantId={tenantId} onClose={() => setShowCreateChannel(false)} />
          )}

          {channelsQuery.isLoading && <div className="empty-state">Loading…</div>}
          {channelsQuery.error && (
            <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading channels.</div>
          )}
          {!channelsQuery.isLoading && (channelsQuery.data?.data ?? []).length === 0 && (
            <div className="empty-state">No channels configured.</div>
          )}
          {(channelsQuery.data?.data ?? []).length > 0 && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Enabled</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(channelsQuery.data?.data ?? []).map(ch => (
                    <tr key={ch.id}>
                      <td style={{ fontWeight: 500 }}>{ch.name}</td>
                      <td>
                        <span style={{
                          background: 'var(--color-surface-2)',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 12,
                        }}>
                          {ch.type}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${ch.enabled ? 'active' : 'decommissioned'}`}>
                          {ch.enabled ? 'enabled' : 'disabled'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {new Date(ch.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <RequirePermission perm="admin:access">
                          <button
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => deleteChannel.mutate(ch.id)}
                          >
                            Delete
                          </button>
                        </RequirePermission>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <>
          {notifQuery.isLoading && <div className="empty-state">Loading…</div>}
          {notifQuery.error && (
            <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading history.</div>
          )}
          {!notifQuery.isLoading && (notifQuery.data?.data ?? []).length === 0 && (
            <div className="empty-state">No notifications sent yet.</div>
          )}
          {(notifQuery.data?.data ?? []).length > 0 && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Device</th>
                    <th>Rule</th>
                    <th>Sent at</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {(notifQuery.data?.data ?? []).map(n => (
                    <tr key={n.id}>
                      <td>
                        <span className={`badge badge-${n.status === 'delivered' ? 'active' : n.status === 'failed' ? 'failed' : 'pending'}`}>
                          {NOTIF_STATUS_LABELS[n.status]}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {n.device_id ? n.device_id.slice(0, 8) + '…' : '—'}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {n.rule_id ? n.rule_id.slice(0, 8) + '…' : '—'}
                      </td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {n.sent_at ? new Date(n.sent_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ color: 'var(--color-danger)', fontSize: 12 }}>
                        {n.error ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
